/* ═══════════════════════════════════════════════════════════════════════════
   eoh-chat-guided.js  ·  Guided, anticipatory answers (chat frontend)

   Renders the AI-composed [[DIDYOUMEAN]] and [[NEXT]] blocks the backend emits:
     • [[DIDYOUMEAN]] — an intro (a "Did you mean:" line OR a direct question) +
       2–5 options the AI wrote itself (label, optional hint, one "recommended"),
       plus "type it myself", "I'm not sure", and "Skip". No fixed categories.
     • [[NEXT]] — after an answer, anticipated next actions as tap options +
       "Nothing else".
   Options render in a tray above the composer; a pick sends the right
   guided_action (one-shot, read by eoh-chat.js) + a short user turn. Voice mode
   speaks the intro and keeps the buttons. Rebuilds on load so nothing shows raw.

   Standalone: chains window.eohOnMessageComplete + window.eohOnConversationRendered.
   Load LAST (after the other chat modules).
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  "use strict";

  function esc(s){ return String(s==null?'':s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }
  function isVoice(){ try { return !!(window.S && window.S.voiceOn); } catch(_){ return false; } }
  var _lastAction = null;

  // ── robustly extract the JSON object from a [[TAG: {…}]] block ─────────────
  function _parse(reply, tag) {
    var r = String(reply || '');
    var start = r.indexOf('[[' + tag + ':');
    if (start === -1) return null;
    var i = r.indexOf('{', start);
    if (i === -1) return null;
    var depth = 0, inStr = false, escd = false, end = -1;
    for (var j = i; j < r.length; j++) {
      var ch = r[j];
      if (inStr) { if (escd) escd = false; else if (ch === '\\') escd = true; else if (ch === '"') inStr = false; continue; }
      if (ch === '"') { inStr = true; continue; }
      if (ch === '{') depth++;
      else if (ch === '}') { depth--; if (depth === 0) { end = j; break; } }
    }
    if (end === -1) return null;
    try { return JSON.parse(r.slice(i, end + 1)); } catch (_) { return null; }
  }
  function _stripBlock(mbubble, tag) {
    try { mbubble.innerHTML = mbubble.innerHTML.replace(new RegExp('\\[\\[' + tag + ':[\\s\\S]*?\\]\\]', 'g'), ''); } catch (_) {}
  }

  // ── tray above the composer ───────────────────────────────────────────────
  function _tray() {
    var t = document.getElementById('eoh-guided-tray');
    if (t) return t;
    var host = document.getElementById('text-input-area');
    t = document.createElement('div'); t.id = 'eoh-guided-tray'; t.style.cssText = 'margin:0 0 10px 0';
    if (host && host.parentNode) host.parentNode.insertBefore(t, host);
    else if (host) host.insertBefore(t, host.firstChild);
    return t;
  }
  function _clearTray(){ var t = document.getElementById('eoh-guided-tray'); if (t) t.remove(); }

  function _btnStyle(variant) {
    var base = 'font-size:12px;line-height:1.25;padding:6px 12px;border-radius:8px;cursor:pointer;font-family:inherit;font-weight:500;transition:filter .12s;text-align:left';
    if (variant === 'skip')  return base + ';border:1px solid rgba(239,68,68,.30);background:rgba(239,68,68,.09);color:#e88c8c';
    if (variant === 'ghost') return base + ';border:1px dashed var(--border2,rgba(255,255,255,.16));background:transparent;color:var(--muted,#9fb0c3)';
    return base + ';border:1px solid var(--border,#22304a);background:rgba(255,255,255,.035);color:var(--text,#e8eef5)';
  }
  // an AI-composed option (label + optional hint + recommended flag)
  function _optBtn(o, onClick) {
    var rec = !!o.recommended;
    var btn = document.createElement('button');
    btn.style.cssText = 'display:flex;flex-direction:column;align-items:flex-start;gap:2px;text-align:left;width:100%;font-size:12.5px;line-height:1.4;padding:9px 11px;border-radius:9px;cursor:pointer;font-family:inherit;border:1px solid '
      + (rec ? 'var(--border-accent,rgba(10,138,95,.5))' : 'var(--border,#22304a)') + ';background:'
      + (rec ? 'rgba(10,138,95,.10)' : 'rgba(255,255,255,.035)') + ';color:var(--text,#e8eef5)';
    var top = esc(o.label || '') + (rec ? ' <span style="font-size:10px;color:var(--gl,#0a8a5f);border:1px solid var(--border-accent,rgba(10,138,95,.5));border-radius:10px;padding:0 6px;margin-left:2px">most likely</span>' : '');
    btn.innerHTML = '<span>' + top + '</span>' + (o.hint ? '<span style="font-size:11px;color:var(--dim,#9fb0c3)">' + esc(o.hint) + '</span>' : '');
    btn.onmouseover = function(){ btn.style.filter = 'brightness(1.12)'; };
    btn.onmouseout  = function(){ btn.style.filter = 'none'; };
    btn.onclick = onClick;
    return btn;
  }
  function _plainBtn(label, variant, onClick) {
    var b = document.createElement('button'); b.style.cssText = _btnStyle(variant); b.textContent = label;
    b.onmouseover = function(){ b.style.filter = 'brightness(1.12)'; };
    b.onmouseout  = function(){ b.style.filter = 'none'; };
    b.onclick = onClick; return b;
  }

  function _freeform(placeholder, onSend, onBack) {
    var t = _tray();
    var card = document.createElement('div');
    card.style.cssText = 'border:1px solid var(--border-accent,rgba(10,138,95,.38));background:var(--bg2,#131a26);border-radius:10px;padding:9px 10px';
    card.innerHTML = '<div style="font-size:11.5px;color:var(--dim,#9fb0c3);margin-bottom:7px">' + esc(placeholder) + '</div>';
    var ta = document.createElement('textarea'); ta.rows = 2;
    ta.style.cssText = 'width:100%;font-size:13px;background:var(--bg3,#111a2b);border:1px solid var(--border,#22304a);border-radius:7px;color:var(--text,#e8eef5);padding:8px;box-sizing:border-box;font-family:inherit;resize:vertical';
    card.appendChild(ta);
    var row = document.createElement('div'); row.style.cssText = 'display:flex;gap:6px;margin-top:7px';
    if (typeof onBack === 'function') row.appendChild(_plainBtn('\u2190 Back', 'default', function(){ onBack(); }));
    var send = document.createElement('button');
    send.style.cssText = 'font-size:12px;font-weight:500;padding:6px 14px;border-radius:8px;border:1px solid transparent;background:var(--gl,#0a8a5f);color:#fff;cursor:pointer';
    send.textContent = 'Send';
    send.onclick = function(){ var v = ta.value.trim(); if (!v){ ta.focus(); return; } onSend(v); };
    row.appendChild(send); card.appendChild(row);
    t.innerHTML = ''; t.appendChild(card); ta.focus();
  }

  // ── send a guided turn (one-shot guided_action read by eoh-chat.js) ────────
  function _send(action, message) {
    _lastAction = (action && action.kind) || null;
    window._eohGuidedAction = action;
    _clearTray();
    if (typeof window.sendMsg_fromWelcome === 'function') window.sendMsg_fromWelcome(message);
    else { var ta = document.getElementById('chat-ta'); if (ta) { ta.value = message; var b = document.getElementById('send-btn'); if (b && !b.disabled) b.click(); } }
  }

  // ── interactive options tray (DIDYOUMEAN or NEXT) ─────────────────────────
  function _renderOptions(o, kind) {
    var t = _tray();
    var card = document.createElement('div');
    card.style.cssText = 'border:1px solid var(--border-accent,rgba(10,138,95,.38));background:var(--bg2,#131a26);border-radius:10px;padding:10px 11px';
    if (o.intro) card.innerHTML = '<div style="font-size:12.5px;line-height:1.45;color:var(--text,#e8eef5);margin-bottom:9px;font-weight:500">' + esc(o.intro) + '</div>';
    var col = document.createElement('div'); col.style.cssText = 'display:flex;flex-direction:column;gap:6px';
    (o.options || []).slice(0, 5).forEach(function (op) {
      col.appendChild(_optBtn(op, function () { _send({ kind: (kind === 'next' ? 'next' : 'pick'), text: op.label }, op.label); }));
    });
    card.appendChild(col);

    var foot = document.createElement('div'); foot.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px;margin-top:9px';
    if (kind === 'next') {
      foot.appendChild(_plainBtn('Nothing else', 'skip', function () { _clearTray(); }));
    } else {
      if (o.allow_freetext !== false) {
        foot.appendChild(_plainBtn('Something else — type it', 'ghost', function () {
          _freeform('Tell me what you mean, in your own words:', function (v) { _send({ kind: 'pick', text: v }, v); }, function () { _renderOptions(o, kind); });
        }));
      }
      foot.appendChild(_plainBtn('I\u2019m not sure', 'default', function () { _send({ kind: 'pick', text: 'I\u2019m not sure — please go with the most likely option.' }, 'I\u2019m not sure'); }));
      foot.appendChild(_plainBtn('Skip', 'skip', function () { _send({ kind: 'skip' }, 'Skip — just answer directly.'); }));
    }
    card.appendChild(foot);
    t.innerHTML = ''; t.appendChild(card);
  }

  // ── bubble content for the DIDYOUMEAN message (raw block replaced) ─────────
  function _bubbleIntro(mbubble, intro) {
    mbubble.innerHTML = '<div style="font-size:13px;line-height:1.55;color:var(--text,#e8eef5)"><span style="color:var(--gl,#0a8a5f)">\uD83D\uDCAC</span> ' + esc(intro || 'Did you mean\u2026') + '</div>';
  }

  // ── styling for step answers ──────────────────────────────────────────────
  function _ensureCss() {
    if (document.getElementById('eoh-guided-css')) return;
    var css =
      '.eoh-guided-steps h1,.eoh-guided-steps h2,.eoh-guided-steps h3{margin:16px 0 8px;line-height:1.3;font-weight:600}' +
      '.eoh-guided-steps h2{font-size:15px}' +
      '.eoh-guided-steps h3{font-size:13.5px;color:var(--text,#e8eef5);border-top:1px solid var(--border,#22304a);padding-top:12px;margin-top:16px}' +
      '.eoh-guided-steps h3:first-child{border-top:none;padding-top:0;margin-top:2px}' +
      '.eoh-guided-steps p{margin:6px 0;line-height:1.6}' +
      '.eoh-guided-steps ol,.eoh-guided-steps ul{margin:6px 0;padding-left:20px}' +
      '.eoh-guided-steps li{margin:3px 0;line-height:1.55}' +
      '.eoh-guided-steps hr{border:none;border-top:1px solid var(--border,#22304a);margin:14px 0}' +
      '.eoh-guided-steps code{background:var(--bg3,#1c2135);padding:1.5px 6px;border-radius:5px;font-size:12px}' +
      '.eoh-guided-steps table{border-collapse:separate;border-spacing:0;width:100%;margin:10px 0 14px;border:1px solid var(--border,#22304a);border-radius:8px;overflow:hidden;font-size:12.5px}' +
      '.eoh-guided-steps th{background:var(--bg3,#1c2135);color:var(--muted,#9fb0c3);text-align:left;padding:7px 11px;font-weight:600;font-size:10.5px;text-transform:uppercase;letter-spacing:.04em;border-bottom:1px solid var(--border,#22304a)}' +
      '.eoh-guided-steps td{padding:7px 11px;border-bottom:1px solid var(--border,#22304a);vertical-align:top;line-height:1.5}' +
      '.eoh-guided-steps tr:last-child td{border-bottom:none}' +
      '.eoh-guided-steps tbody tr:nth-child(even) td{background:rgba(255,255,255,.02)}' +
      '.eoh-guided-next{margin-top:14px;padding-top:12px;border-top:1px solid var(--border,#22304a)}' +
      '.eoh-guided-next .lbl{font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:var(--dim,#9fb0c3);margin-bottom:6px}' +
      '.eoh-guided-next .op{display:inline-block;font-size:12px;border:1px solid var(--border,#22304a);border-radius:16px;padding:3px 10px;margin:0 5px 5px 0;color:var(--muted,#9fb0c3)}';
    var st = document.createElement('style'); st.id = 'eoh-guided-css'; st.textContent = css;
    (document.head || document.documentElement).appendChild(st);
  }

  // static (non-interactive) renders for loaded/historical messages
  function _staticDidYouMean(o) {
    var h = '<div style="font-size:13px;line-height:1.55;margin-bottom:7px"><span style="color:var(--gl,#0a8a5f)">\uD83D\uDCAC</span> ' + esc(o.intro || 'Did you mean\u2026') + '</div>';
    h += '<div style="display:flex;flex-direction:column;gap:5px">' + (o.options || []).slice(0, 5).map(function (op) {
      return '<div style="font-size:12.5px;padding:7px 10px;border-radius:8px;border:1px solid ' + (op.recommended ? 'var(--border-accent,rgba(10,138,95,.5))' : 'var(--border,#22304a)') + ';background:' + (op.recommended ? 'rgba(10,138,95,.08)' : 'transparent') + ';color:var(--dim,#9fb0c3)">' + esc(op.label || '') + (op.hint ? ' <span style="font-size:11px;opacity:.8">— ' + esc(op.hint) + '</span>' : '') + '</div>';
    }).join('') + '</div>';
    return h;
  }
  function _staticNext(n) {
    return '<div class="eoh-guided-next"><div class="lbl">' + esc(n.intro || 'Suggested next steps') + '</div>' +
      (n.options || []).slice(0, 5).map(function (op) { return '<span class="op">' + esc(op.label || '') + '</span>'; }).join('') + '</div>';
  }

  // ── live completion hook ──────────────────────────────────────────────────
  function _onComplete(bubble, data) {
    if (!bubble || !data) return;
    var mbubble = bubble.querySelector ? bubble.querySelector('.mbubble') : null;
    var reply = data.reply || '';
    var action = _lastAction; _lastAction = null;

    var dym = _parse(reply, 'DIDYOUMEAN');
    if (dym) {
      if (mbubble) { _bubbleIntro(mbubble, dym.intro); mbubble.setAttribute('data-guided', '1'); }
      _renderOptions(dym, 'didyoumean');
      if (isVoice() && typeof window.speakText === 'function') { try { window.speakText(dym.intro || 'Please choose an option.'); } catch (_) {} }
      return;
    }

    var nxt = _parse(reply, 'NEXT');
    if (nxt || action === 'pick' || action === 'answer' || action === 'next') {
      _ensureCss();
      if (mbubble) mbubble.classList.add('eoh-guided-steps');
    }
    if (nxt) {
      if (mbubble) { _stripBlock(mbubble, 'NEXT'); mbubble.setAttribute('data-guided', '1'); }
      _renderOptions(nxt, 'next');
    }
  }

  // ── rebuild from stored messages on load / switch / refresh ───────────────
  function _processLoaded() {
    var msgs = document.getElementById('msgs'); if (!msgs) return;
    Array.prototype.forEach.call(msgs.querySelectorAll('.mbubble'), function (mb) {
      if (mb.getAttribute('data-guided')) return;
      var txt = mb.textContent || '';
      if (txt.indexOf('[[DIDYOUMEAN:') !== -1) {
        var o = _parse(txt, 'DIDYOUMEAN');
        if (o) { mb.innerHTML = _staticDidYouMean(o); mb.setAttribute('data-guided', '1'); return; }
      }
      if (txt.indexOf('[[NEXT:') !== -1) {
        var n = _parse(txt, 'NEXT');
        _stripBlock(mb, 'NEXT'); _ensureCss(); mb.classList.add('eoh-guided-steps');
        if (n) mb.insertAdjacentHTML('beforeend', _staticNext(n));
        mb.setAttribute('data-guided', '1');
      }
    });
  }

  function _chain() {
    var prev = window.eohOnMessageComplete;
    window.eohOnMessageComplete = function (bubble, data) {
      try { if (typeof prev === 'function') prev(bubble, data); } catch (_) {}
      try { _onComplete(bubble, data); } catch (_) {}
    };
    var prevR = window.eohOnConversationRendered;
    window.eohOnConversationRendered = function (sess) {
      try { if (typeof prevR === 'function') prevR(sess); } catch (_) {}
      try { _clearTray(); } catch (_) {}
      try { _processLoaded(); } catch (_) {}
    };
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', _chain); else _chain();
})();
